/**
 * =============================================================================
 * Card validation & resume navigation (Intake V2)
 * =============================================================================
 *
 * validateCard — returns an error when a step’s required fields are missing.
 * resolveResumeCardIndex — after API hydrate, finds the first card the patient
 *   still needs to complete (skips verification/success/celebration cards).
 */

import { msg } from "@lingui/macro";
import type { I18n } from "@lingui/core";
import type { IntakeData } from "@/lib/types";
import type { ProcedureType } from "@/lib/specialtyTags";
import {
  getNationalForDisplay,
  validateNationalPhone,
  type PhoneValidationError,
} from "@/lib/phoneNumber";

export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function coerceToStringArray(v: unknown): string[] {
  if (Array.isArray(v)) return v as string[];
  if (typeof v === "string" && v.trim()) return [v.trim()];
  return [];
}

function phoneValidationMessage(error: PhoneValidationError, i18n: I18n): string {
  switch (error) {
    case "required":
      return i18n._(msg`Phone number is required.`);
    case "unsupported_country":
      return i18n._(msg`Please select a valid country code.`);
    case "invalid":
    default:
      return i18n._(
        msg`Please enter a valid mobile number for your country.`,
      );
  }
}

/** Returns an error message or field-level errors when the card is incomplete. */
export function validateCard(
  cardId: string,
  data: IntakeData,
  i18n: I18n,
): string | Record<string, string> | undefined {
  switch (cardId) {
    case "fullName": {
      const errs: Record<string, string> = {};
      if (!data.firstName?.trim()) {
        errs.firstName = i18n._(msg`First name is required.`);
      }
      if (!data.lastName?.trim()) {
        errs.lastName = i18n._(msg`Last name is required.`);
      }
      return Object.keys(errs).length > 0 ? errs : undefined;
    }
    case "contactInfo": {
      const errs: Record<string, string> = {};
      if (!data.country?.trim()) {
        errs.country = i18n._(msg`Please select a country code.`);
      } else {
        const phoneResult = validateNationalPhone(
          getNationalForDisplay(data.phone ?? "", data.country),
          data.country,
        );
        if (!phoneResult.valid) {
          errs.phone = phoneValidationMessage(phoneResult.error, i18n);
        }
      }
      if (!data.email?.trim()) {
        errs.email = i18n._(msg`Email is required.`);
      } else if (!emailRegex.test(data.email)) {
        errs.email = i18n._(msg`Please enter a valid email.`);
      }
      return Object.keys(errs).length > 0 ? errs : undefined;
    }
    case "preferredLanguage":
      return data.preferredLanguage
        ? undefined
        : i18n._(msg`Please select a language.`);
    case "dentalProcedureType": {
      const arr = coerceToStringArray(data.dentalProcedureType);
      return arr.length > 0
        ? undefined
        : i18n._(msg`Please select a dental procedure.`);
    }
    case "otherSpecialtyFocus": {
      const arr = coerceToStringArray(data.otherSpecialtyFocus);
      return arr.length > 0
        ? undefined
        : i18n._(msg`Please select at least one option.`);
    }
    case "hairTransplantRelevantFactors": {
      const arr = coerceToStringArray(data.hairTransplantRelevantFactors);
      return arr.length > 0
        ? undefined
        : i18n._(msg`Please select at least one option.`);
    }
    case "hairTransplantTreatmentAreas": {
      const arr = coerceToStringArray(data.hairTransplantTreatmentAreas);
      if (arr.length === 0) {
        return i18n._(msg`Please select at least one area.`);
      }
      if (
        arr.includes("other") &&
        !data.hairTransplantTreatmentAreasOther?.trim()
      ) {
        return i18n._(
          msg`Please describe the area or treatment you have in mind.`,
        );
      }
      return undefined;
    }
    case "procedures": {
      const slug =
        typeof data.procedures === "string"
          ? data.procedures
          : (data.procedures as ProcedureType)?.slug ?? "";
      return slug ? undefined : i18n._(msg`Please select a procedure.`);
    }
    default:
      return undefined;
  }
}

function isFieldEmpty(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

const RESUME_SKIP_CARD_IDS = new Set([
  "verification",
  "success",
  "submit-celebration",
  "other-specialty-thank-you",
  "other-specialty-review",
]);

/**
 * Walk the card flow in order; stop at the first incomplete step.
 * If everything through specialty/questions is done, land on review.
 */
export function resolveResumeCardIndex(
  cards: Array<{ id: string; fieldKey?: keyof IntakeData; isOptional?: boolean }>,
  data: IntakeData,
  i18n: I18n,
): number {
  for (let i = 0; i < cards.length; i++) {
    const card = cards[i];
    if (RESUME_SKIP_CARD_IDS.has(card.id)) continue;
    if (card.id === "review") break;

    const validationError = validateCard(card.id, data, i18n);
    if (validationError) return i;

    if (card.fieldKey && !card.isOptional && isFieldEmpty(data[card.fieldKey])) {
      return i;
    }
  }

  const reviewIdx = cards.findIndex((c) => c.id === "review");
  return reviewIdx >= 0 ? reviewIdx : Math.max(0, cards.length - 1);
}
